import { observer } from 'mobx-react';
import { useEffect, useMemo, MouseEvent } from "react";
import { useMedia, useStore } from 'shared/hooks';
import { IUserDashboardWidgetItemModel, IUserDashboardGroup, UserDashboardWidgetModel } from "shared/models";
import { PPDWidgetItemValueProgress } from "../PPDWidgetItemValueProgress";
import { PPDWidgetBonusBadge } from '../PPDWidgetBonusBadge';
import { PPDWidgetStoresTable } from '../PPDWidgetStoresTable';
import { PPDWidgetBonusModal } from '../PPDWidgetBonusModal';
import { PPDWidgetBaseHeader } from '../PPDWidgetBaseHeader';
import { PPDWidgetBaseProperties } from '../PPDWidgetBaseProperties';
import { PPDWidgetStoresList } from '../PPDWidgetStoresList';
import { PPDWidgetBaseStoreModal } from '../PPDWidgetBaseStoreModal';
import { PPDWidgetGroupSelect } from '../PPDWidgetGroupSelect';
import { PPDWidgetStoresModal } from '../PPDWidgetStoresModal';
import { profileDashboardStore } from './../../../../store/ProfileDashBoardStore';
import classnames from 'classnames';
import { PPDWidgetEffeciencyModal } from '../PPDWidgetEfficiencyModal';
import { useDashboardDict } from './../../../../context/useDashboardDict';
import { getMonoGroupId, isMonoStore } from './../../../../utilities/isMonoStore';

import './styles.scss';
import { UserDashboardGroupType } from 'shared/models/UserDashboardModel.ts';

type PropsType = {
    widget: UserDashboardWidgetModel
};

export const PPDWidgetBase = observer(({ widget }: PropsType) => {
    const store = useStore(() => ({
        groupId: null as number | string | null,
        storeId: null as number | string | null,
        itemGroups: [] as IUserDashboardGroup[],
        positionId: null as number | string | null,
        selectedItem: null as null | IUserDashboardWidgetItemModel,
        isOpenStoresModal: false,
        isOpenContent: false,
        isOpenBonusModal: false,
        isExpandedList: false,
        isOpenEfficiencyModal: false,
    }));

    const { stores = [], positions = [], groups = {} } = useDashboardDict() || {};

    const MAX_VISIBLE_ITEMS = 3;
    const { isMobileOrTablet } = useMedia();

    useEffect(() => {
        const type = widget?.items_group_type;
        if (!groups || !type) {
            store.set("itemGroups", []);
            return;
        }
        const allGroups = groups[type as keyof typeof groups] || [];
        const filteredGroups = allGroups.filter(g =>
            (widget.items || []).some(i => i.group_id === g.id)
        );
        store.set("itemGroups", filteredGroups);

        if (!store.groupId && filteredGroups.length) {
            store.set("groupId", filteredGroups[0].id);
        }
    }, [groups, store, widget?.items_group_type, widget.items]);

    useEffect(() => {
        if (!store.positionId && positions.length) {
            store.set("positionId", positions[0].id);
        }
    }, [positions, store]);

    const hasTable = useMemo(() => widget?.stores_view === "list", [widget]);

    const item = useMemo(() => {
        const itemsSafe = widget.items || [];
        if (store.itemGroups.length > 0 && store.groupId) {
            return itemsSafe.find(i => i.group_id === store.groupId && i.store_id === null) ?? null;
        }
        return itemsSafe.find(i => i.store_id === null) ?? null;
    }, [store.groupId, store.itemGroups.length, widget.items]);

    const toggleExpanded = () => {
        isMobileOrTablet ? store.toggle("isExpandedList") : profileDashboardStore.toggleExpandedAllLists();
    };

    const isExpanded = isMobileOrTablet ? store.isExpandedList : profileDashboardStore.isExpandedAllLists;

    const filteredItems = useMemo(() => {
        const itemsSafe = widget.items || [];
        if (store.itemGroups.length > 0 && store.groupId) {
            return itemsSafe.filter(i => i.group_id === store.groupId && i.store_id !== null);
        }
        return itemsSafe.filter(i => i.store_id !== null);
    }, [store.groupId, store.itemGroups.length, widget.items]);

    const visibleItems =
        useMemo(() => isExpanded
            ? filteredItems
            : filteredItems.slice(0, MAX_VISIBLE_ITEMS),
        [isExpanded, filteredItems]);

    const hasBoxContent = (store.itemGroups.length > 0 && store.groupId) || !!widget?.bonuses?.items?.length;

    const onChangeGroupInStoreModal = (groupId: number | string) => {
        store.set("groupId", groupId);

        const newItem = widget.items.find(
            i => i.group_id === groupId && i.store_id  === store.selectedItem?.store_id
        ) ?? null;

        store.set("selectedItem", newItem);
    };

    if (!item) return null;

    return (
        <>
            <div className='ppd-widget-base'>
                <PPDWidgetBaseHeader
                    name={widget.name || '-'}
                    className="ppd-widget-base__header"
                    isOpen={store.isOpenContent}
                    onToggle={() => store.toggle("isOpenContent")}
                    isShowOptions={stores.length > 0 && widget?.stores_view === "modal"}
                    onClickOptions={(event: MouseEvent) => {
                        event.stopPropagation();
                        store.set("isOpenStoresModal", true);
                    }}
                />
                {(!isMobileOrTablet || store.isOpenContent) && (
                    <>
                        <div className={classnames('ppd-widget-base__content', {
                            'ppd-widget-base__content--no-box': !hasBoxContent,
                        })}>
                            {hasBoxContent ? (
                                <div className="ppd-widget-base__box">
                                    {store.itemGroups.length > 0 && store.groupId && (
                                        <PPDWidgetGroupSelect
                                            items={store.itemGroups}
                                            value={store.groupId}
                                            onChange={(id) => store.set("groupId", id)}
                                        />
                                    )}

                                    {!!widget?.bonuses?.items?.length && (
                                        <div className="ppd-widget-base__bonus-badge">
                                            <PPDWidgetBonusBadge
                                                isActive={widget?.bonuses?.status_id === 'received'}
                                                onClick={() => store.set("isOpenBonusModal", true)}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            <div className="ppd-widget-base__wrapper">
                                <PPDWidgetItemValueProgress
                                    size={isMobileOrTablet ? 140 : 182}
                                    strokeWidth={isMobileOrTablet ? 19 : 24}
                                    progress={item.value?.amount ?? 0}
                                />
                            </div>

                            <PPDWidgetBaseProperties item={item} labels={widget.labels || {}}>
                                {(widget?.efficiency?.items?.length || 0) > 0 && (
                                    <button
                                        className="ppd-widget-base__properties-button"
                                        type="button"
                                        onClick={() => store.set("isOpenEfficiencyModal", true)}
                                    >
                                        {isMobileOrTablet ? (widget.labels?.efficiency_short || "Эффективность") : (widget.labels?.efficiency_full || "Эффективность")}
                                    </button>
                                )}
                            </PPDWidgetBaseProperties>
                        </div>

                        {hasTable && visibleItems.length > 0 && (
                            <div className="ppd-widget-base__bottom">
                                {isMobileOrTablet ? (
                                    <PPDWidgetStoresList
                                        items={visibleItems}
                                        onSelect={row => store.set("selectedItem", row)}
                                        valueType="detailed"
                                    />
                                ) : (
                                    <PPDWidgetStoresTable
                                        items={visibleItems}
                                        labels={widget.labels || {}}
                                        valueType="detailed"
                                    />
                                )}

                                {filteredItems.length > MAX_VISIBLE_ITEMS && (
                                    <div className={classnames("ppd-widget-base__expand", {
                                        "ppd-widget-base__expand--open": isExpanded
                                    })}
                                    >
                                        <button className="ppd-widget-base__expand-button" onClick={toggleExpanded}>
                                            {isExpanded ? "Свернуть" : "Развернуть"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {store.selectedItem && isMobileOrTablet && (
                <PPDWidgetBaseStoreModal
                    item={store.selectedItem}
                    onClose={() => store.set("selectedItem", null)}
                    labels={widget.labels || {}}
                    groups={ store.itemGroups }
                    groupId={
                        getMonoGroupId(
                            store.selectedItem.store_id,
                            widget.items,
                            groups?.[widget.items_group_type as UserDashboardGroupType] ?? []
                        ) ?? store.groupId
                    }
                    isMonoStore={isMonoStore(
                        store.selectedItem.store_id,
                        widget.items,
                        groups?.[widget.items_group_type as UserDashboardGroupType] ?? []
                    )}
                    onChangeGroup={onChangeGroupInStoreModal}
                />
            )}

            <PPDWidgetBonusModal
                isOpen={store.isOpenBonusModal}
                onClose={() => store.set("isOpenBonusModal", false)}
                groups={widget.bonuses?.groups || []}
                items={widget.bonuses?.items || []}
                total={widget.bonuses?.total}
                labels={widget.labels || {}}
            />

            <PPDWidgetStoresModal
                title={widget.stores_modal_title || ""}
                isOpen={store.isOpenStoresModal}
                onClose={() => store.set("isOpenStoresModal", false)}
                items={filteredItems}
                labels={widget.labels || {}}
                groups={store.itemGroups}
                groupId={store.groupId}
                onChangeGroup={(id) => store.set("groupId", id)}
                onSelect={row => store.set("selectedItem", row)}
                stickyHeader={true}
            />

            <PPDWidgetEffeciencyModal
                isOpen={store.isOpenEfficiencyModal}
                onClose={() => store.set("isOpenEfficiencyModal", false)}
                title={widget?.labels?.efficiency_full || ""}
                items={widget.efficiency?.items || []}
                positionId={store.groupId}
                labels={widget.labels || {}}
                onChangePosition={(id) => {
                    store.set("groupId", id)
                }}
            />
        </>
    );
});
